'use client';

import Icon from '@/components/Common/Icons/Icon';
import { NextUI } from '@/components/NextUI';
import { FileType, SvgIcons } from '@/types/types';
import clsx from 'clsx';
import React, { forwardRef, useImperativeHandle } from 'react';
import ToolInfoPopover from './ToolInfoPopover';
import { useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import useCustomSearchParams from '@/hooks/useCustomSearchParams';
import { formatAnalyticsSource } from '@/helpers/common';
import { useMemo } from 'react';
import { buildUploadFormatDisplay } from '@/features/tool/utils/uploadConstraints';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { INVALID_FILE_FORMAT_ERROR_TEXT } from '@/features/tool/constants/uploadErrors';
import useTrackBtnUpload from '@/features/tool/hooks/useTrackBtnUpload';

export interface DropzoneHandle {
  open: () => void;
}

interface DropzoneProps {
  status?: 'active' | 'inactive' | 'error';
  variant?: 'default' | 'app';
  fileSize?: number;
  icon?: SvgIcons | React.ReactNode;
  text?: string;
  className?: string;
  subText?: React.ReactNode;
  background?: string;
  buttonText?: string;
  buttonEndContent?: React.ReactNode;
  buttonStartContent?: React.ReactNode;
  errorComponent?: React.ReactNode;
  routerPath?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onError?: (error: string) => void;
  classNames?: {
    base?: string;
    dashed?: string;
  };
  isMultipleFile?: boolean;
  fileTypes?: FileType[];
  toolType?: string;
}

// Component for handling file drag & drop and file selection functionality
const Dropzone = forwardRef<DropzoneHandle, DropzoneProps>(
  (
    {
      status = 'active',
      variant = 'default',
      fileSize = 100,
      icon,
      text,
      className,
      subText,
      background,
      buttonText,
      buttonEndContent,
      buttonStartContent,
      errorComponent,
      routerPath,
      classNames,
      fileTypes = [],
      isMultipleFile = false,
      toolType,
      onChange,
      onError,
    },
    ref
  ) => {
    const t = useTranslations('components.dropzone');
    // Generate accepted file types string for input element (e.g., '.pdf, .doc')
    const acceptedFileTypes =
      fileTypes.length !== 0
        ? fileTypes.map((type) => `.${type}`).join(', ')
        : '.pdf';
    const supportedFormats = useMemo(
      () => buildUploadFormatDisplay(fileTypes),
      [fileTypes]
    );
    const active = status === 'active';
    const fileRef = React.useRef<HTMLInputElement>(null);
    const [dragging, setDragging] = React.useState(false);

    const { getParam } = useCustomSearchParams();
    const pathname = usePathname();

    const formattedToolType = useMemo(() => {
      if (toolType) return formatAnalyticsSource(toolType);
      const t = getParam('tool') || pathname.split('/').filter(Boolean).pop();
      return formatAnalyticsSource(t);
    }, [toolType, getParam, pathname]);
    const trackBtnUpload = useTrackBtnUpload(formattedToolType);

    useImperativeHandle(ref, () => ({
      open: () => {
        trackBtnUpload();
        fileRef.current?.click();
      },
    }));

    // Handle file selection through input element
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files || e.target.files.length === 0) {
        (fileRef.current as HTMLInputElement).value = '';
        return;
      }

      // Validate file types before processing
      if (!validateFileTypes(e.target.files)) {
        onError?.(INVALID_FILE_FORMAT_ERROR_TEXT);
        return;
      }

      // Validate file size before processing
      // if (!validateFileSize(e.target.files)) {
      //   props.onError?.('File size exceeds the limit');
      //   return;
      // }

      onChange?.(e);

      // Clear the file input value after processing
      setTimeout(() => {
        if (fileRef.current) (fileRef.current as HTMLInputElement).value = '';
        setDragging(false);
      }, 250);
      setDragging(false);
    };

    // Trigger file input click when button is clicked
    const handleClick = () => {
      trackBtnUpload();
      fileRef.current?.click();
    };

    /*
     * Previous split-button upload flow kept here for future reuse.
     *
     * Requires:
     * import DropdownButton from '@/components/NextUI/DropdownButton';
     * import { useGoogleDrivePicker } from '@/hooks/useGoogleDrivePicker';
     * import { useDropboxPicker } from '@/hooks/useDropboxPicker';
     *
     * const handleExternalFile = (files: File[]) => {
     *   if (files) {
     *     const fileList = new DataTransfer();
     *     files.forEach((file) => {
     *       fileList.items.add(file);
     *     });
     *
     *     handleFileChange({
     *       target: {
     *         files: fileList.files,
     *       },
     *     } as React.ChangeEvent<HTMLInputElement>);
     *   }
     * };
     *
     * const { googleDrivePicker } = useGoogleDrivePicker({
     *   onFileChange: handleExternalFile,
     *   settings: {
     *     multiple: isMultipleFile,
     *     mimeTypes: fileTypes,
     *   },
     * });
     *
     * const { dropboxPicker, isLoading: isDropboxLoading } = useDropboxPicker({
     *   onFileChange: handleExternalFile,
     *   settings: {
     *     multiple: isMultipleFile,
     *     mimeTypes: fileTypes,
     *   },
     * });
     *
     * const onSelectListItem = (type: string) => {
     *   if (type === 'device') {
     *     handleClick();
     *   } else if (type === 'google-drive') {
     *     googleDrivePicker();
     *   } else if (type === 'dropbox') {
     *     dropboxPicker();
     *   }
     * };
     */

    // Handle dragover event to show visual feedback
    const handleDragOver = (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setDragging(true);
    };

    // Validate if all dropped files match the accepted file types
    const validateFileTypes = (files: FileList): boolean => {
      for (const file of files) {
        const extension = file.name.split('.').pop()?.toLowerCase();
        if (!extension || !fileTypes.includes(extension as FileType)) {
          return false;
        }
      }
      return true;
    };

    // Validate if all dropped files are within the file size limit
    const validateFileSize = (files: FileList): boolean => {
      for (const file of files) {
        if (file.size > fileSize * 1024 * 1024) {
          return false;
        }
      }
      return true;
    };

    // Handle file drop event
    const handleDrop = (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setDragging(false);

      if (fileRef.current) {
        const files = event.dataTransfer.files;

        // Update the file input with dropped files
        fileRef.current.files = event.dataTransfer.files;
        // Trigger file change handler with dropped files
        handleFileChange({
          target: {
            files: event.dataTransfer.files,
          },
        } as React.ChangeEvent<HTMLInputElement>);
      }
    };

    const handleDragLeave = (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setTimeout(() => {
        setDragging(false);
      }, 110);
    };

    return (
      <div
        className={clsx(
          'rounded-extra-lg p-5 transition-opacity',
          active ? 'bg-primary-extra-light-blue' : 'bg-gray-lightest',
          variant === 'app'
            ? '!bg-primary-extra-light-blue2 flex-1 w-full md:!p-10 !pb-0 md:!pb-0 !rounded-none h-full flex flex-col'
            : '',
          {
            'opacity-60': dragging,
          },
          className,
          classNames?.base
        )}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragLeave={handleDragLeave}
      >
        <input
          type="file"
          multiple={isMultipleFile}
          ref={fileRef}
          className="hidden"
          onChange={handleFileChange}
          accept={acceptedFileTypes}
        />
        <div
          className={clsx(
            'rounded-extra-lg p-12 pb-6 flex flex-col items-center',
            active ? 'dashed-border-blue' : 'dashed-border-gray',
            variant === 'app' && active
              ? 'h-full justify-center relative !bg-primary-extra-light-blue hover:cursor-pointer hover:!bg-[#d2e0fc] transition-all'
              : 'h-full justify-center relative hover:cursor-pointer',
            classNames?.dashed
          )}
          onClick={active ? handleClick : undefined}
        >
          {variant === 'app' && <ToolInfoPopover />}
          <div>
            {React.isValidElement(icon) ? (
              icon
            ) : active ? (
              variant === 'default' ? (
                <Icon
                  name={(icon as SvgIcons) || 'UploadFile'}
                  width={172}
                  height={138}
                />
              ) : (
                <Icon
                  name={(icon as SvgIcons) || 'FileUploadApp'}
                  width={172}
                  height={131}
                />
              )
            ) : variant === 'default' ? (
              <Icon
                name={(icon as SvgIcons) || 'UploadFileInactive'}
                width={172}
                height={138}
              />
            ) : (
              <Icon
                name={(icon as SvgIcons) || 'FileUploadAppInactive'}
                width={172}
                height={131}
              />
            )}
          </div>

          <div className="mt-5">
            {/*
             * Previous dropdown CTA kept for future reuse.
             *
             * <DropdownButton
             *   dropdownButtonProps={{
             *     className: clsx(status === 'inactive' && '!hidden'),
             *   }}
             *   buttonProps={{
             *     onClick: handleClick,
             *     className: clsx(
             *       active
             *         ? '!rounded-r-none'
             *         : 'bg-gray-lighter hover:!bg-gray-lighter !w-[216px] !rounded-r-extra',
             *       status === 'inactive' ? '!rounded-r-extra' : '',
             *       isDropboxLoading ? 'loading' : ''
             *     ),
             *     disabled: status === 'inactive' || isDropboxLoading,
             *   }}
             *   dropdownProps={{
             *     classNames: {
             *       trigger: clsx({ 'hidden absolute': !active }),
             *     },
             *     isDisabled: isDropboxLoading,
             *   }}
             *   text={
             *     status === 'active'
             *       ? t('dropdown.choose_files')
             *       : t('dropdown.coming_soon')
             *   }
             *   onSelectItem={onSelectListItem}
             *   loadingStates={{
             *     dropbox: isDropboxLoading,
             *   }}
             * />
             */}
            <NextUI.Button
              endContent={buttonEndContent}
              startContent={
                buttonStartContent ?? (
                  <FontAwesomeIcon width={14} icon="file-import" />
                )
              }
              onClick={(event) => {
                event.stopPropagation();
                handleClick();
              }}
              className={clsx(
                '!w-[216px]',
                !active && 'bg-gray-lighter hover:!bg-gray-lighter'
              )}
              disabled={!active}
            >
              {status === 'active'
                ? buttonText || t('dropdown.choose_files')
                : t('dropdown.coming_soon')}
            </NextUI.Button>
          </div>

          <div
            className={clsx(
              'mt-12 text-gray-dark text-center',
              variant === 'app' && 'mt-4'
            )}
          >
            {active ? (
              <p className="body-semibold">{text || t('default_text')}</p>
            ) : (
              <p
                className="font-semibold"
                dangerouslySetInnerHTML={{
                  __html: t('coming_soon'),
                }}
              />
            )}
          </div>

          <div className={clsx(['mt-4', !active && 'hidden'])}>
            <p
              className={clsx(
                'body2 text-gray-medium text-center',
                variant === 'app' && '!text-[12px]'
              )}
            >
              {subText || (
                <>
                  {t('supported_files_dynamic', { formats: supportedFormats })}
                  <br />
                  {t('supported_formats_dynamic', {
                    formats: supportedFormats,
                  })}
                </>
              )}
            </p>
          </div>
        </div>
        {variant === 'app' && (
          <div className="flex items-center justify-center h-16 my-1">
            <Icon name="SolidBrand" width={128} height={20} />
          </div>
        )}
      </div>
    );
  }
);

Dropzone.displayName = 'Dropzone';

export default Dropzone;
